'use client'

import { useEffect, useState } from "react"
import { ReadonlyURLSearchParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { stringExists } from ".."

export const SEARCH_QUERY_PARAM = 'search'

const getSearchTermFromUrl = (searchParams: ReadonlyURLSearchParams): string => {
    return searchParams.get(SEARCH_QUERY_PARAM) || ''
}

const updateUrlWithSearch = (
    searchTerm: string | undefined,
    pathname: string,
    searchParams: ReadonlyURLSearchParams,
    /** Function to replace the current URL with a new one, typically router.replace */
    replace: (url: string) => void
): void => {
    const params = new URLSearchParams(searchParams.toString())

    if (!stringExists(searchTerm)) {
        params.delete(SEARCH_QUERY_PARAM)
    } else {
        params.set(SEARCH_QUERY_PARAM, searchTerm)
    }

    const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname
    const currentUrl = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname

    // Without this the hook rewrites the url on mount from a snapshot taken at that moment, which
    // races with other query param owners such as the pagination sync and reverts their changes.
    if (newUrl === currentUrl) {
        return
    }

    replace(newUrl)
}

interface Props {
    bindToURL?: boolean
}

interface ReturnType {
    searchTerm: string
    setSearchTerm: (searchTerm: string) => void
}

export const useSearch = ({ bindToURL = true }: Props = {}): ReturnType => {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const router = useRouter()
    const [searchTerm, setSearchTerm] = useState(bindToURL ? getSearchTermFromUrl(searchParams) : '')

    useEffect(() => {
        if (bindToURL) {
            updateUrlWithSearch(searchTerm, pathname, searchParams, router.replace)
        }
    }, [searchTerm])

    return {
        searchTerm,
        setSearchTerm,
    }
}